import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminAuth } from '../services/adminAuth';

const AddProperty: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
    }
  }, [navigate]);

  if (!adminAuth.isAuthenticated()) {
    return null;
  }
  const [property, setProperty] = useState({
    address: '',
    zillow_url: '',
    original_image: '',
    price: '',
    score: '',
    color: '',
    prompt: '',
    pencil_image_url: '',
    oil_image_url: '',
    charcoal_image_url: '',
    chalk_image_url: '',
    status: 'pending'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!adminAuth.isAuthenticated()) {
        throw new Error('You must be logged in to add properties');
      }

      // Convert numeric fields
      const numericProperty = {
        ...property,
        user_id: 'admin', // Use a default admin user ID
        score: property.score ? parseFloat(property.score) : null,
        color: property.color || null,
        prompt: property.prompt || null
      };

      const { error: supabaseError } = await supabase
        .from('properties')
        .insert([numericProperty]);

      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        throw new Error(
          supabaseError.message === 'new row violates row-level security policy for table "properties"'
            ? 'You must be logged in to add properties'
            : supabaseError.message
        );
      }

      navigate('/admin');
    } catch (err) {
      console.error('Error adding property:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while adding the property');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProperty(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">
            Add New Property
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                required
                value={property.address}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter property address"
              />
            </div>

            <div>
              <label htmlFor="zillow_url" className="block text-sm font-medium text-gray-700 mb-1">
                Zillow URL
              </label>
              <input
                type="url"
                id="zillow_url"
                name="zillow_url"
                value={property.zillow_url}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://www.zillow.com/..."
              />
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={property.price}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="$500,000"
              />
            </div>

            <div>
              <label htmlFor="original_image" className="block text-sm font-medium text-gray-700 mb-1">
                Original Image URL
              </label>
              <input
                type="url"
                id="original_image"
                name="original_image"
                value={property.original_image}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="score" className="block text-sm font-medium text-gray-700 mb-1">
                  Score
                </label>
                <input
                  type="number"
                  id="score"
                  name="score"
                  value={property.score}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  value={property.color}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">AI Generated Images</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="pencil_image_url" className="block text-sm font-medium text-gray-700 mb-1">
                  Pencil Image URL
                </label>
                <input
                  type="url"
                  id="pencil_image_url"
                  name="pencil_image_url"
                  value={property.pencil_image_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label htmlFor="oil_image_url" className="block text-sm font-medium text-gray-700 mb-1">
                  Oil Image URL
                </label>
                <input
                  type="url"
                  id="oil_image_url"
                  name="oil_image_url"
                  value={property.oil_image_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label htmlFor="charcoal_image_url" className="block text-sm font-medium text-gray-700 mb-1">
                  Charcoal Image URL
                </label>
                <input
                  type="url"
                  id="charcoal_image_url"
                  name="charcoal_image_url"
                  value={property.charcoal_image_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label htmlFor="chalk_image_url" className="block text-sm font-medium text-gray-700 mb-1">
                  Chalk Image URL
                </label>
                <input
                  type="url"
                  id="chalk_image_url"
                  name="chalk_image_url"
                  value={property.chalk_image_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={property.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
               <option value="SOLD">Sold</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Link
              to="/admin"
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Adding...' : 'Add Property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProperty;